<template>
  <header class="navbar" :class="{ 'navbar--scrolled': isScrolled }">
    <div class="navbar__container">
      <!-- Logo -->
      <router-link to="/" class="navbar__logo">
        <img src="/logo.png" alt="KernelPilot Logo" class="navbar__logo-img" />
        <span class="navbar__logo-text">Kernel Pilot</span>
      </router-link>

      <!-- Desktop Navigation -->
      <nav class="navbar__nav">
        <router-link 
          v-for="link in navLinks" 
          :key="link.path"
          :to="link.path"
          class="navbar__link"
          :class="{ 'navbar__link--active': isActive(link.path) }"
        >
          {{ link.label }}
        </router-link>
        <a 
          href="https://github.com/KernelPilot"
          target="_blank"
          rel="noopener noreferrer"
          class="navbar__link navbar__link--github"
        >
          <svg class="navbar__github-icon" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
          </svg>
          GitHub
        </a>
      </nav>

      <!-- Language Switcher -->
      <div class="navbar__actions">
        <button 
          @click="toggleLanguage" 
          class="navbar__lang-btn"
          :title="t('common.language')"
        >
          <span class="navbar__lang-current">{{ currentLang === 'en' ? 'EN' : '中' }}</span>
        </button>

        <!-- Mobile Menu Button -->
        <button 
          @click="toggleMobileMenu" 
          class="navbar__mobile-btn"
          :aria-label="isMobileMenuOpen ? 'Close menu' : 'Open menu'"
        >
          <span class="navbar__mobile-icon" :class="{ 'navbar__mobile-icon--open': isMobileMenuOpen }">
            <span></span>
            <span></span>
            <span></span>
          </span>
        </button>
      </div>
    </div>

    <!-- Mobile Navigation -->
    <nav 
      class="navbar__mobile-nav" 
      :class="{ 'navbar__mobile-nav--open': isMobileMenuOpen }"
    >
      <router-link 
        v-for="link in navLinks" 
        :key="link.path"
        :to="link.path"
        class="navbar__mobile-link"
        @click="closeMobileMenu"
      >
        {{ link.label }}
      </router-link>
      <a 
        href="https://github.com/KernelPilot"
        target="_blank"
        rel="noopener noreferrer"
        class="navbar__mobile-link"
      >
        GitHub
      </a>
    </nav>
  </header>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { setLanguage, getCurrentLanguage } from '../i18n'

const { t } = useI18n()
const route = useRoute()

// State
const isScrolled = ref(false)
const isMobileMenuOpen = ref(false)
const currentLang = ref(getCurrentLanguage())

// Navigation links
const navLinks = computed(() => [
  { path: '/', label: t('nav.home') },
  { path: '/generation', label: t('nav.generation') },
  { path: '/optimization', label: t('nav.optimization') },
  { path: '/blog', label: t('nav.blog') }
])

// Check if link is active
const isActive = (path: string): boolean => {
  if (path === '/') {
    return route.path === '/'
  }
  return route.path.startsWith(path)
}

// Toggle mobile menu
const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value
  document.body.style.overflow = isMobileMenuOpen.value ? 'hidden' : ''
}

const closeMobileMenu = () => {
  isMobileMenuOpen.value = false
  document.body.style.overflow = ''
}

// Toggle language
const toggleLanguage = () => {
  const newLang = currentLang.value === 'en' ? 'zh' : 'en'
  setLanguage(newLang)
  currentLang.value = newLang
}

// Scroll handler
const handleScroll = () => {
  isScrolled.value = window.scrollY > 20
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll)
  handleScroll()
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})
</script>

<style scoped>
.navbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: var(--z-fixed);
  padding: var(--spacing-4) 0;
  transition: all var(--transition-base);
}

.navbar--scrolled {
  background: var(--color-bg-glass);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border-bottom: 1px solid var(--color-border-light);
  padding: var(--spacing-3) 0;
}

.navbar__container {
  max-width: var(--container-xl);
  margin: 0 auto;
  padding: 0 var(--spacing-6);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

/* Logo */
.navbar__logo {
  display: flex;
  align-items: center;
  gap: var(--spacing-3);
  text-decoration: none;
}

.navbar__logo-img {
  width: 50px;
  height: auto;
  transition: transform var(--transition-base);
}

.navbar__logo:hover .navbar__logo-img {
  transform: scale(1.05);
}

.navbar__logo-text {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-primary);
}

/* Desktop Navigation */
.navbar__nav {
  display: flex;
  align-items: center;
  gap: var(--spacing-8);
}

.navbar__link {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-secondary);
  text-decoration: none;
  position: relative;
  padding: var(--spacing-2) 0;
  transition: color var(--transition-fast);
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
}

.navbar__link::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 0;
  height: 2px;
  background: var(--gradient-primary);
  transition: width var(--transition-base);
}

.navbar__link:hover,
.navbar__link--active {
  color: var(--color-text-primary);
}

.navbar__link:hover::after,
.navbar__link--active::after {
  width: 100%;
}

.navbar__github-icon {
  width: 18px;
  height: 18px;
}

/* Actions */
.navbar__actions {
  display: flex;
  align-items: center;
  gap: var(--spacing-4);
}

.navbar__lang-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: var(--radius-md);
  background: var(--color-bg-tertiary);
  border: 1px solid var(--color-border);
  color: var(--color-text-primary);
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-sm);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.navbar__lang-btn:hover {
  background: var(--color-primary);
  border-color: var(--color-primary);
  box-shadow: var(--shadow-glow-primary);
}

/* Mobile Menu Button */
.navbar__mobile-btn {
  display: none;
  width: 32px;
  height: 32px;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
}

.navbar__mobile-icon {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 24px;
  height: 18px;
}

.navbar__mobile-icon span {
  display: block;
  width: 100%;
  height: 2px;
  background: var(--color-text-primary);
  border-radius: 2px;
  transition: all var(--transition-fast);
}

.navbar__mobile-icon--open span:nth-child(1) {
  transform: translateY(8px) rotate(45deg);
}

.navbar__mobile-icon--open span:nth-child(2) {
  opacity: 0;
}

.navbar__mobile-icon--open span:nth-child(3) {
  transform: translateY(-8px) rotate(-45deg);
}

/* Mobile Navigation */
.navbar__mobile-nav {
  display: none;
  position: fixed;
  top: 60px;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--color-bg-primary);
  padding: var(--spacing-8);
  flex-direction: column;
  gap: var(--spacing-4);
  transform: translateX(100%);
  transition: transform var(--transition-base);
}

.navbar__mobile-nav--open {
  transform: translateX(0);
}

.navbar__mobile-link {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-secondary);
  text-decoration: none;
  padding: var(--spacing-4);
  border-radius: var(--radius-md);
  transition: all var(--transition-fast);
}

.navbar__mobile-link:hover {
  color: var(--color-text-primary);
  background: var(--color-bg-secondary);
}

/* Responsive */
@media (max-width: 768px) {
  .navbar__nav {
    display: none;
  }

  .navbar__mobile-btn {
    display: block;
  }

  .navbar__mobile-nav {
    display: flex;
  }
}
</style>
